/**
 * Spread/total line-number rules.
 *
 * These mirror the exchange's server-side validation in go-api
 * `place/validation.go` (validateSpreadNumber / validateTotalNumber /
 * validateSpreadTotalNumberMultiple). Keep the two in sync.
 *
 * Why this exists on the client: an order at an invalid line still comes back
 * HTTP 200 — the rejection is buried in `data.createdSessions[].error` with
 * `errorType: 'validation_error'`. Without a client-side check the user types
 * a number, hits place, and the failure is silent.
 */

const MAX_LINE = 1000;

/** Half-point everywhere except soccer, which trades quarter-point lines. */
export function lineStep(game) {
  return game?.sport === 'soccer' ? 0.25 : 0.5;
}

/** Round a raw entry onto the nearest legal increment for the sport. */
export function snapLine(value, step) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n / step) * step;
}

/**
 * @returns {{ ok: boolean, error?: string }} — `error` is user-facing copy.
 */
export function validateLineNumber({ value, type, game }) {
  const n = Number(value);
  if (value === '' || value === null || !Number.isFinite(n)) {
    return { ok: false, error: 'Enter a number' };
  }

  const step = lineStep(game);
  const isTotal = type === 'total';

  if (isTotal && n <= 0) {
    return { ok: false, error: 'Totals must be above 0' };
  }
  if (Math.abs(n) > MAX_LINE) {
    return { ok: false, error: `Must be between -${MAX_LINE} and ${MAX_LINE}` };
  }
  // Modulo on floats is exact enough here: every legal line is a multiple of
  // 0.25, all of which are representable.
  if (Math.abs(n / step - Math.round(n / step)) > 1e-9) {
    return { ok: false, error: `Must be a multiple of ${step}` };
  }
  return { ok: true };
}
